import { useState } from 'react';
import { CheckCircle2, Loader2, MoreHorizontal, RotateCcw, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  testingApi,
  TICKET_STATUS_LABEL,
  type TicketDto,
  type TicketPatchInput,
  type TicketStatus,
} from '../../lib/api/testing';

/**
 * Was sich an einem Ticket erledigen lässt, ohne es zu öffnen.
 *
 * Vorher musste man für jede Kleinigkeit in die Detailansicht und wieder zurück
 * — bei zehn nachgetesteten Tickets sind das zwanzig Seitenwechsel für zwanzig
 * Klicks Arbeit. Die Statuskette kommt vom Server (`allowedStatus`), damit sie
 * nicht ein zweites Mal im Browser steht und auseinanderläuft.
 */

type Props = Readonly<{
  ticket: TicketDto;
  /** Ob die angemeldete Person das Ticket schon hat. */
  gehoertMir: boolean;
  /** Nach jeder Änderung: die Liste neu holen. */
  onGeaendert: () => void;
}>;

export default function SchnellAktionen({ ticket, gehoertMir, onGeaendert }: Props) {
  const [laeuft, setLaeuft] = useState(false);
  // `allowedStatus` beginnt mit dem aktuellen Stand — die Auswahl in der
  // Detailansicht zeigt ihn als gewählt. Im Schnellmenü wäre er ein Eintrag,
  // der nichts tut.
  const erlaubt = (ticket.allowedStatus ?? []).filter((stand) => stand !== ticket.status);

  const aendern = async (felder: TicketPatchInput, erfolg: string) => {
    setLaeuft(true);
    try {
      await testingApi.patch(ticket.id, felder);
      toast.success(erfolg);
      onGeaendert();
    } catch (fehler) {
      toast.error(
        fehler instanceof Error ? fehler.message : 'Die Änderung wurde nicht übernommen.'
      );
    } finally {
      setLaeuft(false);
    }
  };

  // Aus „Nachtest in Ordnung" ist der Abschluss der einzige sinnvolle Schritt —
  // dafür gibt es einen eigenen Eintrag statt eines Umwegs über die Statusliste.
  const kannSchliessen = ticket.status === 'nachtest_ok' && erlaubt.includes('erledigt');
  // Geschlossen heisst gesperrt; der Weg zurück führt über „triagiert" und
  // landet im Verlauf.
  const kannOeffnen = ticket.closed === true && erlaubt.includes('triagiert');
  // `allowedStatus` lässt „Dublette" schon weg, solange kein Ziel gewählt ist —
  // hier wird nichts zusätzlich gefiltert, sonst stünde die Statuskette zweimal
  // da und liefe auseinander.
  const weitereStaende = erlaubt.filter(
    (stand) => !(kannSchliessen && stand === 'erledigt') && !(kannOeffnen && stand === 'triagiert')
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={`Aktionen für ${ticket.label}`}
          disabled={laeuft}
          // Die Zeile selbst öffnet das Ticket — das Menü soll das nicht auslösen.
          onClick={(ereignis) => ereignis.stopPropagation()}
        >
          {laeuft ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MoreHorizontal className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(ereignis) => ereignis.stopPropagation()}>
        <DropdownMenuLabel className="font-mono text-xs">{ticket.label}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {!ticket.closed && !gehoertMir && (
          <DropdownMenuItem
            onSelect={() => void aendern({ assignedAccountId: 'mir' }, 'Dir zugewiesen.')}
          >
            <UserCheck className="mr-2 h-4 w-4" />
            Mir zuweisen
          </DropdownMenuItem>
        )}
        {!ticket.closed && ticket.assignedAccountId && (
          <DropdownMenuItem
            onSelect={() =>
              void aendern({ assignedAccountId: null }, 'Zuständigkeit entzogen.')
            }
          >
            <UserCheck className="mr-2 h-4 w-4" />
            Zuständigkeit entziehen
          </DropdownMenuItem>
        )}

        {kannSchliessen && (
          <DropdownMenuItem
            onSelect={() => void aendern({ status: 'erledigt' }, 'Abgeschlossen.')}
          >
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Schließen
          </DropdownMenuItem>
        )}
        {kannOeffnen && (
          <DropdownMenuItem
            onSelect={() => void aendern({ status: 'triagiert' }, 'Wieder geöffnet.')}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Wieder öffnen
          </DropdownMenuItem>
        )}

        {weitereStaende.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Status setzen
            </DropdownMenuLabel>
            {weitereStaende.map((stand) => (
              <DropdownMenuItem
                key={stand}
                onSelect={() =>
                  void aendern(
                    { status: stand as TicketStatus },
                    `Status: ${TICKET_STATUS_LABEL[stand as TicketStatus]}`
                  )
                }
              >
                {TICKET_STATUS_LABEL[stand as TicketStatus] ?? stand}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
